import React from "react";
import { Input, Button, Card, Typography, message, Select, Modal } from "antd";
import { UserOutlined, GlobalOutlined, GoogleOutlined, ThunderboltOutlined } from '@ant-design/icons';
import Avatar from './Avatar';
import { useTranslation } from 'react-i18next';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { initializeWebSocket } from "../Socket";
import { getProviderById, buildOAuthUrl, getEnabledProviders } from '../utils/oauthProviders';
import store from "../store";
import { setSinglePlayer, setAuthStatus, setGameMode } from "../actions";

const { Title, Text } = Typography;
const { Option } = Select;

interface LoginState {
  username: string;
  room: string;
  isLoading: boolean;
  connectionStatus: 'ready' | 'connecting' | 'connected' | 'error' | 'disconnected';
  oauthLoading: string | null;
  recentRooms: string[];
  showUsernameModal: boolean;
  tempUsername: string;
  currentStep: 'auth' | 'gamemode' | 'roomselect';
  authType: string | 'guest' | null;
  gameMode: 'single' | 'multiplayer' | null;
}

interface LoginProps {
  t: any;
  i18n: any;
}

class LoginComponent extends React.Component<LoginProps, LoginState> {
  private loginTimeoutId: NodeJS.Timeout | null = null;
  private usernameInputRef: React.RefObject<any> = React.createRef();
  private roomInputRef: React.RefObject<any> = React.createRef();

  constructor(props: any) {
    super(props);
    
    const savedUsername = localStorage.getItem('tea-garden-username') || '';
    const savedRooms = JSON.parse(localStorage.getItem('tea-garden-recent-rooms') || '[]');
    
    this.state = {
      username: savedUsername,
      room: "",
      isLoading: false,
      connectionStatus: 'ready',
      oauthLoading: null,
      recentRooms: savedRooms,
      showUsernameModal: false,
      tempUsername: savedUsername,
      currentStep: 'auth',
      authType: null,
      gameMode: null
    };
  }

  componentDidMount() {
    (window as any).loginComponent = this;
    
    // Check authentication status on mount
    this.checkAuthenticationStatus();
    
    // Check for deeplink flow
    this.checkDeeplinkFlow();
    
    // Set up URL listener for OAuth callbacks on mobile
    if (Capacitor.isNativePlatform()) {
      App.addListener('appUrlOpen', (event) => {
        console.log('🚀 App URL opened:', event.url);
        this.handleUrlOpen(event.url);
      });
      
      Browser.addListener('browserFinished', () => {
        console.log('🔍 Browser finished - checking for OAuth result');
        this.checkOAuthResult();
      });
    }
  }

  componentWillUnmount() {
    delete (window as any).loginComponent;
    if (this.loginTimeoutId) {
      clearTimeout(this.loginTimeoutId);
      this.loginTimeoutId = null;
    }
    
    // Clean up listeners
    if (Capacitor.isNativePlatform()) {
      App.removeAllListeners();
      Browser.removeAllListeners();
    }
  }

  // Check if this is a deeplink flow and skip directly to game
  checkDeeplinkFlow = () => {
    const state = store.getState();
    const { deeplinkRoomId, deeplinkInviteCode, isDeeplinkFlow } = state.kingGame.gameState;
    
    if (isDeeplinkFlow && (deeplinkRoomId || deeplinkInviteCode)) {
      console.log('Deeplink flow detected, checking authentication...');
      // If authenticated, proceed directly to join room
      // If not authenticated, stay on auth step but show deeplink info
    }
  };

  // Check authentication status from backend
  checkAuthenticationStatus = async () => {
    try {
      // First check if we already have local session data
      const sessionData = localStorage.getItem('kingGameSession');
      if (sessionData) {
        const parsedSession = JSON.parse(sessionData);
        const { authType, username, timestamp } = parsedSession;
        
        // Check if session is not too old (24 hours)
        const sessionAge = Date.now() - timestamp;
        const maxAge = 24 * 60 * 60 * 1000;
        
        if (sessionAge < maxAge && authType && username) {
          console.log('Found valid local session:', { authType, username });
          
          if (authType === 'guest') {
            // For guest users, trust the local session
            this.setState({ 
              currentStep: 'gamemode',
              authType: 'guest',
              username: username
            });
            store.dispatch(setAuthStatus(true, 'guest'));
            
            // Check for deeplink and auto-join if available
            setTimeout(() => this.checkForDirectJoin(), 100);
            return;
          }
        }
      }
      
      // Check for JWT token (mobile auth)
      const jwtToken = localStorage.getItem('tea-garden-jwt-token');
      if (jwtToken) {
        console.log('Found JWT token, validating...');
        const userData = await this.verifyJwtToken(jwtToken);
        
        if (userData && userData.authenticated) {
          console.log('User is authenticated with JWT token:', userData);
          
          this.setState({ 
            currentStep: 'gamemode',
            authType: userData.provider || 'google',
            username: userData.username || userData.name || userData.displayName || 'User'
          });
          
          store.dispatch(setAuthStatus(true, userData.provider || 'google'));
          
          // Check for deeplink and auto-join if available
          setTimeout(() => this.checkForDirectJoin(), 100);
          return;
        } else {
          // Invalid token, remove it
          console.log('Invalid JWT token, removing...');
          localStorage.removeItem('tea-garden-jwt-token');
        }
      }
      
      // For Google auth, check with backend (cookie-based)
      const isDevelopment = process.env.NODE_ENV === 'development';
      const backendUrl = isDevelopment ? 'http://localhost:8080' : 'https://gamegarden.co';
      
      const response = await fetch(`${backendUrl}/api/auth/user`, {
        credentials: 'include',
        mode: 'cors'
      });
      
      if (response.ok) {
        const userData = await response.json();
        console.log('User is authenticated with Google:', userData);
        
        // User is already authenticated with OAuth
        this.setState({ 
          currentStep: 'gamemode',
          authType: userData.provider || 'google',
          username: userData.username || userData.name || userData.displayName || 'User'
        });
        
        store.dispatch(setAuthStatus(true, userData.provider || 'google'));
        
        // Check for deeplink and auto-join if available
        setTimeout(() => this.checkForDirectJoin(), 100);
      } else {
        console.log('User is not authenticated');
        this.setState({ currentStep: 'auth' });
        store.dispatch(setAuthStatus(false, null));
      }
    } catch (error) {
      console.log('Authentication check failed:', error);
      this.setState({ currentStep: 'auth' });
      store.dispatch(setAuthStatus(false, null));
    }
  };

  // Handle OAuth login
  handleOAuthLogin = async (providerId: string) => {
    const providerConfig = getProviderById(providerId);
    if (!providerConfig) {
      message.error(`${providerId} login is not available`);
      return;
    }

    this.setState({ oauthLoading: providerId });
    
    const isMobile = Capacitor.isNativePlatform();
    const oauthUrl = buildOAuthUrl(providerId, '', false, isMobile);
    
    try {
      if (Capacitor.isNativePlatform()) {
        // Mobile: Use Capacitor Browser
        console.log('📱 Opening OAuth URL in mobile browser:', oauthUrl);
        message.loading(`Opening ${providerConfig.displayName} login...`, 2);
        
        await Browser.open({ 
          url: oauthUrl,
          windowName: `${providerId}_oauth`,
          toolbarColor: providerConfig.colors.bg,
          presentationStyle: 'popover'
        });
        
        console.log('✅ OAuth browser opened, waiting for callback...');
        message.info('Complete login in browser, then return to app', 8);

      } else {
        // Web: Use traditional popup method
        this.handleWebOAuthLogin(oauthUrl, providerConfig);
      }
    } catch (error) {
      console.error('OAuth login error:', error);
      message.error(`Failed to open ${providerConfig.displayName} login page`);
      this.setState({ oauthLoading: null });
    }
  };

  // Web OAuth handling
  handleWebOAuthLogin = (oauthUrl: string, providerConfig: any) => {
    const popup = window.open(
      oauthUrl,
      `${providerConfig.id}_oauth`,
      'width=500,height=600,scrollbars=yes,resizable=yes'
    );

    if (!popup) {
      message.error('Popup blocked. Please allow popups for OAuth login.');
      this.setState({ oauthLoading: null });
      return;
    }

    let isOAuthComplete = false;
    let cleanupTimeoutId: NodeJS.Timeout;
    
    const cleanup = () => {
      window.removeEventListener('message', messageListener);
      if (cleanupTimeoutId) {
        clearTimeout(cleanupTimeoutId);
      }
      this.setState({ oauthLoading: null });
    };

    const messageListener = (event: MessageEvent) => {
      if (isOAuthComplete) return;

      console.log('OAuth popup message received:', {
        origin: event.origin,
        data: event.data,
        type: event.data?.type
      });

      const isDevelopment = process.env.NODE_ENV === 'development';
      const expectedOrigins = isDevelopment 
        ? ['http://localhost:8080', 'http://localhost:3000'] 
        : ['https://gamegarden.co', 'https://www.gamegarden.co'];
      
      const isValidOrigin = expectedOrigins.includes(event.origin);
      if (!isValidOrigin) {
        console.warn('OAuth message from invalid origin:', event.origin, 'Expected:', expectedOrigins);
        return;
      }

      if (event.data.type === 'OAUTH_SUCCESS') {
        isOAuthComplete = true;
        try { popup.close(); } catch (e) {}
        this.handleOAuthSuccessWithData(event.data.data);
        cleanup();
      } else if (event.data.type === 'OAUTH_ERROR') {
        isOAuthComplete = true;
        const errorMessage = event.data.data?.error || event.data.error || 'Unknown error occurred';
        message.error(`${providerConfig.displayName} login failed: ${errorMessage}`);
        try { popup.close(); } catch (e) {}
        cleanup();
      }
    };

    window.addEventListener('message', messageListener);
    
    cleanupTimeoutId = setTimeout(() => {
      if (!isOAuthComplete) {
        message.warning('OAuth login timed out. Please try again.');
        try { popup.close(); } catch (e) {}
        cleanup();
      }
    }, 60000);
  };

  // Handle successful OAuth with data from popup
  handleOAuthSuccessWithData = async (authData: any) => {
    try {
      console.log('OAuth success with data:', authData);
      
      if (authData && authData.success && authData.user) {
        const username = authData.user.name || authData.user.email || 'User';
        const authProvider = this.state.oauthLoading || authData.user.provider || 'google';
        
        this.setState({
          currentStep: 'gamemode',
          authType: authProvider,
          username: username,
          oauthLoading: null
        });
        
        store.dispatch(setAuthStatus(true, authProvider));
        message.success(`Welcome ${username}!`);
        
        // If deeplink flow, proceed directly to join room
        setTimeout(() => this.checkForDirectJoin(), 100);
      } else {
        // Fallback to API verification if data is incomplete
        await this.handleOAuthSuccess();
      }
    } catch (error: any) {
      console.error('OAuth success handling failed:', error);
      // Fallback to API verification
      await this.handleOAuthSuccess();
    }
  };

  // Handle successful OAuth (fallback method)
  handleOAuthSuccess = async () => {
    try {
      const userData = await this.verifyAuthentication();
      
      if (userData && userData.authenticated) {
        const username = userData.username || userData.name || userData.displayName || 'User';
        const authProvider = this.state.oauthLoading || 'google';
        
        this.setState({
          currentStep: 'gamemode',
          authType: authProvider,
          username: username,
          oauthLoading: null
        });
        
        store.dispatch(setAuthStatus(true, authProvider));
        message.success(`Welcome ${username}!`);
        
        // If deeplink flow, proceed directly to join room
        setTimeout(() => this.checkForDirectJoin(), 100);
      } else {
        throw new Error('Failed to verify authentication');
      }
    } catch (error: any) {
      message.error('Failed to complete login: ' + (error.message || error));
      this.setState({ oauthLoading: null });
    }
  };

  // Handle JWT token success (mobile)
  handleJwtTokenSuccess = async (token: string) => {
    try {
      const userData = await this.verifyJwtToken(token);
      
      if (userData && userData.authenticated) {
        const username = userData.username || userData.name || userData.displayName || 'User';
        const authProvider = this.state.oauthLoading || 'google';
        
        this.setState({
          currentStep: 'gamemode',
          authType: authProvider,
          username: username,
          oauthLoading: null
        });
        
        store.dispatch(setAuthStatus(true, authProvider));
        message.success(`Welcome ${username}!`);
        
        // If deeplink flow, proceed directly to join room
        setTimeout(() => this.checkForDirectJoin(), 100);
      } else {
        throw new Error('Failed to verify JWT token');
      }
    } catch (error: any) {
      message.error('Failed to complete Google login: ' + (error.message || error));
      this.setState({ oauthLoading: null });
    }
  };

  // Verify authentication with backend
  verifyAuthentication = async (): Promise<any> => {
    const isDevelopment = process.env.NODE_ENV === 'development';
    const backendUrl = isDevelopment ? 'http://localhost:8080' : 'https://gamegarden.co';
    
    try {
      const response = await fetch(`${backendUrl}/api/auth/user`, {
        credentials: 'include',
        mode: 'cors'
      });
      
      if (response.ok) {
        const userData = await response.json();
        return userData;
      }
      return null;
    } catch (error) {
      console.error('Error verifying authentication:', error);
      return null;
    }
  };

  // Verify JWT token with backend
  verifyJwtToken = async (token: string): Promise<any> => {
    const isDevelopment = process.env.NODE_ENV === 'development';
    const backendUrl = isDevelopment ? 'http://localhost:8080' : 'https://gamegarden.co';
    
    try {
      const response = await fetch(`${backendUrl}/api/auth/validate-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
        mode: 'cors'
      });
      
      if (response.ok) {
        const userData = await response.json();
        return userData;
      }
      return null;
    } catch (error) {
      console.error('Error verifying JWT token:', error);
      return null;
    }
  };

  // Handle guest login
  handleGuestLogin = () => {
    const guestUsername = this.state.username || this.generateRandomUsername();
    
    this.setState({
      currentStep: 'gamemode',
      authType: 'guest',
      username: guestUsername,
      tempUsername: guestUsername
    });
    
    // Save the username to localStorage
    this.saveUsername(guestUsername);
    
    store.dispatch(setAuthStatus(true, 'guest'));
    
    // If deeplink flow, proceed directly to join room
    setTimeout(() => this.checkForDirectJoin(), 100);
  };

  // Check if we should directly join a room (deeplink flow)
  checkForDirectJoin = () => {
    const state = store.getState();
    const { deeplinkRoomId, isDeeplinkFlow } = state.kingGame.gameState;
    
    if (isDeeplinkFlow && deeplinkRoomId && this.state.authType) {
      console.log('Direct joining room from deeplink:', deeplinkRoomId);
      // Set multiplayer mode and skip room selection
      this.setState({ 
        room: deeplinkRoomId,
        gameMode: 'multiplayer'
      });
      store.dispatch(setGameMode('multiplayer'));
      this.joinRoom(deeplinkRoomId, false); // false = multiplayer
    }
  };

  // Handle game mode selection
  handleGameModeSelect = (mode: 'single' | 'multiplayer') => {
    this.setState({ gameMode: mode });
    store.dispatch(setGameMode(mode));
    
    if (mode === 'single') {
      this.handleSinglePlayerStart();
    } else {
      this.setState({ currentStep: 'roomselect' });
      setTimeout(() => {
        if (this.roomInputRef.current) {
          this.roomInputRef.current.focus();
        }
      }, 100);
    }
  };

  // Handle single player start
  handleSinglePlayerStart = async () => {
    const username = this.state.authType === 'guest' 
      ? (this.state.tempUsername || this.generateRandomUsername())
      : this.state.username;
    
    this.setState({ isLoading: true, username });
    this.saveUsername(username);
    
    try {
      const roomName = this.generateUUID();
      await this.joinRoom(roomName, true, username);
      
    } catch (error) {
      this.setState({ isLoading: false });
      message.error("Failed to start single player game");
    }
  };

  // Handle multiplayer room join
  handleRoomJoin = () => {
    if (!this.state.room.trim()) {
      message.error("Please enter a room name");
      return;
    }

    if (this.state.authType === 'guest') {
      // Show username modal for guest
      this.setState({ 
        showUsernameModal: true,
        tempUsername: this.state.username || this.generateRandomUsername()
      });
    } else {
      // Direct join for authenticated user
      this.joinRoom(this.state.room, false);
    }
  };

  // Handle username modal confirmation
  handleUsernameConfirm = () => {
    const username = this.state.tempUsername.trim();
    if (!username) {
      message.error("Please enter a username");
      return;
    }
    
    this.setState({ 
      username: username,
      showUsernameModal: false
    });
    
    this.saveUsername(username);
    this.joinRoom(this.state.room, false, username);
  };

  // Join room (main join logic)
  joinRoom = async (roomName: string, singlePlayer: boolean, username?: string) => {
    const finalUsername = username || this.state.username;
    
    this.setState({ isLoading: true });
    this.saveRecentRoom(roomName);
    
    try {
      const ws = await this.initializeConnection();
      
      const joinData = {
        id: "joinRoom",
        name: finalUsername,
        room: roomName,
        singlePlayer: singlePlayer,
        oauthProvider: this.state.authType !== 'guest' ? this.state.authType : null,
        isAuthenticated: this.state.authType !== 'guest',
        isGuest: this.state.authType === 'guest'
      };
      
      store.dispatch(setSinglePlayer(singlePlayer));
      store.dispatch(setGameMode(singlePlayer ? 'single' : 'multiplayer'));
      
      (window as any).pendingLogin = {
        username: finalUsername,
        room: roomName,
        authProvider: this.state.authType !== 'guest' ? this.state.authType : null,
        isGuest: this.state.authType === 'guest'
      };
      
      ws.send(JSON.stringify(joinData));
      
      const gameType = singlePlayer ? 'single player' : 'multiplayer';
      message.success(`Welcome ${finalUsername}! Joining ${gameType} game...`);
      
    } catch (error) {
      this.setState({ isLoading: false });
      message.error("Failed to connect to game server");
    }

    this.loginTimeoutId = setTimeout(() => {
      if (this.state.isLoading) {
        this.setState({ isLoading: false });
        message.error("Connection timeout. Please try again.");
      }
      this.loginTimeoutId = null;
    }, 10000);
  };

  // Initialize WebSocket connection
  initializeConnection = async (): Promise<WebSocket> => {
    this.setState({ connectionStatus: 'connecting' });
    
    try {
      const ws = await initializeWebSocket();
      
      ws.onopen = () => {
        this.setState({ connectionStatus: 'connected' });
      };

      ws.onerror = (error) => {
        this.setState({ connectionStatus: 'error' });
        message.error("Failed to connect to game server");
      };

      ws.onclose = () => {
        this.setState({ connectionStatus: 'disconnected' });
      };
      
      if ((window as any).app && (window as any).app.setupWebSocketHandlers) {
        (window as any).app.setupWebSocketHandlers(ws);
      }
      
      return ws;
    } catch (error) {
      this.setState({ connectionStatus: 'error' });
      throw error;
    }
  };

  // Utility functions
  generateUUID = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  };

  generateRandomUsername = (): string => {
    const adjectives = ['Swift', 'Clever', 'Brave', 'Wise', 'Lucky', 'Bold', 'Sharp', 'Quick', 'Smart', 'Cool'];
    const nouns = ['Player', 'Gamer', 'King', 'Ace', 'Hero', 'Master', 'Pro', 'Star', 'Winner', 'Champion'];
    const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    const number = Math.floor(Math.random() * 99) + 1;
    return `${adjective}${noun}${number}`;
  };

  saveUsername = (username: string) => {
    if (username.trim()) {
      localStorage.setItem('tea-garden-username', username.trim());
    }
  };

  saveRecentRoom = (room: string) => {
    if (room.trim()) {
      const recentRooms = [...this.state.recentRooms];
      const roomName = room.trim();
      
      const index = recentRooms.indexOf(roomName);
      if (index > -1) {
        recentRooms.splice(index, 1);
      }
      recentRooms.unshift(roomName);
      
      const updatedRooms = recentRooms.slice(0, 5);
      
      localStorage.setItem('tea-garden-recent-rooms', JSON.stringify(updatedRooms));
      this.setState({ recentRooms: updatedRooms });
    }
  };

  // OAuth mobile callback handlers (keeping existing logic)
  handleUrlOpen = (url: string) => {
    console.log('🔗 Handling URL open:', url);
    
    if (url.includes('teagarden://oauth-callback')) {
      console.log('✅ OAuth callback detected via URL scheme');
      Browser.close().catch(e => console.log('ℹ️ Browser already closed:', e));
      this.processOAuthCallback(url);
    }
  };

  checkOAuthResult = async () => {
    try {
      console.log('🔍 Checking OAuth result in localStorage...');
      const oauthResult = localStorage.getItem('tea-garden-oauth-success');
      
      if (oauthResult) {
        console.log('✅ Found OAuth success in localStorage:', oauthResult);
        localStorage.removeItem('tea-garden-oauth-success');
        
        if (this.state.oauthLoading) {
          await this.handleOAuthSuccess();
        }
      }
    } catch (error) {
      console.error('❌ Error checking OAuth result:', error);
      this.setState({ oauthLoading: null });
    }
  };

  processOAuthCallback = async (url: string) => {
    try {
      const urlObj = new URL(url);
      const status = urlObj.searchParams.get('status');
      const error = urlObj.searchParams.get('error');
      const token = urlObj.searchParams.get('token');
      
      if (error) {
        message.error(`OAuth login failed: ${error}`);
        this.setState({ oauthLoading: null });
        return;
      }
      
      if (status === 'success' && token) {
        const providerConfig = getProviderById(this.state.oauthLoading || 'google');
        message.success(`${providerConfig?.displayName || 'OAuth'} login successful! Connecting...`);
        
        // Store JWT token for mobile authentication
        localStorage.setItem('tea-garden-jwt-token', token);
        
        setTimeout(async () => {
          await this.handleJwtTokenSuccess(token);
        }, 1500);
      } else if (status === 'success') {
        // Fallback to cookie-based auth for backward compatibility
        setTimeout(async () => {
          await this.handleOAuthSuccess();
        }, 1500);
      }
    } catch (error) {
      console.error('❌ Error processing OAuth callback:', error);
      message.error('Failed to process OAuth callback. Please try again.');
      this.setState({ oauthLoading: null });
    }
  };

  clearLoginTimeout = () => {
    if (this.loginTimeoutId) {
      clearTimeout(this.loginTimeoutId);
      this.loginTimeoutId = null;
    }
    this.setState({ isLoading: false, oauthLoading: null });
  };

  // Navigation handlers
  handleBackToAuth = () => {
    this.setState({ currentStep: 'auth', authType: null });
    store.dispatch(setAuthStatus(false, null));
  };

  handleBackToGameMode = () => {
    this.setState({ currentStep: 'gamemode', gameMode: null });
  };

  handleLanguageChange = (language: string) => {
    this.props.i18n.changeLanguage(language);
  };

  render() {
    const { currentStep, authType, gameMode, room, isLoading, oauthLoading } = this.state;
    const { i18n } = this.props;
    
    const isAnyLoading = isLoading || oauthLoading !== null;
    const state = store.getState();
    const { deeplinkRoomId, isDeeplinkFlow } = state.kingGame.gameState;

    return (
      <div 
        className="login-container"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%)',
          padding: '20px'
        }}
      >
        <Card
          className="glass-effect login-card"
          style={{
            width: '100%',
            maxWidth: '380px',
            borderRadius: 'var(--radius-xl)',
            boxShadow: 'var(--shadow-xl)',
            border: '1px solid var(--border-color)',
            background: 'rgba(30, 41, 59, 0.9)',
            backdropFilter: 'blur(20px)'
          }}
        >
          {/* Language Selector */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <Select
              value={i18n.language}
              onChange={this.handleLanguageChange}
              style={{ width: 120 }}
              suffixIcon={<GlobalOutlined style={{ color: 'var(--text-muted)' }} />}
            >
              <Option value="tr">🇹🇷 Türkçe</Option>
              <Option value="en">🇺🇸 English</Option>
            </Select>
          </div>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <img
              src="/game_garden_logo_bg.png"
              alt="Game Garden Logo"
              style={{
                width: '180px',
                height: 'auto',
                marginBottom: 16,
                filter: 'drop-shadow(0 8px 24px rgba(0, 0, 0, 0.3))'
              }}
            />
            <Title level={2} className="text-gradient" style={{ margin: 0, marginBottom: 8 }}>
              King Card Game
            </Title>
            {isDeeplinkFlow && deeplinkRoomId && (
              <Text style={{ color: 'var(--accent-color)', fontSize: '0.9rem', display: 'block' }}>
                🔗 Joining room: {deeplinkRoomId}
              </Text>
            )}
          </div>

          {/* Step 1: Authentication */}
          {currentStep === 'auth' && (
            <div>
              <div style={{ marginBottom: 24, textAlign: 'center' }}>
                <Text style={{ color: 'var(--text-secondary)', fontSize: '1rem' }}>
                  {this.props.t('login.chooseHowToContinue')}
                </Text>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {getEnabledProviders().map((provider) => (
                  <Button
                    key={provider.id}
                    type="primary"
                    size="large"
                    onClick={() => this.handleOAuthLogin(provider.id)}
                    loading={oauthLoading === provider.id}
                    disabled={isAnyLoading}
                    icon={!oauthLoading && provider.id === 'google' ? <GoogleOutlined /> : (!oauthLoading ? (
                      typeof provider.icon === 'string' && provider.icon.startsWith('/') ? 
                        <img src={provider.icon} alt={provider.displayName} style={{ width: 16, height: 16 }} /> :
                        provider.icon
                    ) : undefined)}
                    className="mobile-button"
                    style={{
                      minHeight: '56px',
                      background: `linear-gradient(135deg, ${provider.colors.bg}, ${provider.colors.hover})`,
                      border: 'none',
                      borderRadius: 'var(--radius-lg)',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      boxShadow: `0 8px 24px ${provider.colors.bg}30`,
                      transition: 'all 0.2s ease',
                      color: provider.colors.text || '#ffffff'
                    }}
                  >
                    {oauthLoading === provider.id 
                      ? this.props.t('login.signingIn') 
                      : `Continue with ${provider.displayName}`
                    }
                  </Button>
                ))}

                <Button
                  size="large"
                  onClick={this.handleGuestLogin}
                  disabled={isAnyLoading}
                  icon={<UserOutlined />}
                  className="mobile-button"
                  style={{
                    minHeight: '56px',
                    background: 'linear-gradient(135deg, #64748b, #475569)',
                    borderColor: '#64748b',
                    color: 'white',
                    borderRadius: 'var(--radius-lg)',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    boxShadow: '0 8px 24px rgba(100, 116, 139, 0.3)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {this.props.t('login.continueAsGuest')}
                </Button>
              </div>

              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <Text style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  OAuth login saves your progress across devices
                </Text>
              </div>
            </div>
          )}

          {/* Step 2: Game Mode Selection */}
          {currentStep === 'gamemode' && (
            <div>
              <div style={{ marginBottom: 24 }}>
                <Button
                  type="text"
                  onClick={this.handleBackToAuth}
                  style={{ color: 'var(--text-secondary)', padding: 0, marginBottom: 16 }}
                >
                  {this.props.t('login.back')}
                </Button>
                
                {authType !== 'guest' && this.state.username && (
                  <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    <div style={{ marginBottom: 8 }}>
                      <Avatar
                        name={this.state.username}
                        size={52}
                        isCurrentPlayer={false}
                        showBorder={true}
                      />
                    </div>
                    <div>
                      <Text style={{ 
                        color: 'var(--text-primary)', 
                        fontSize: '1rem',
                        fontWeight: 500,
                        display: 'block'
                      }}>
                        {this.state.username}
                      </Text>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Button
                  type="primary"
                  size="large"
                  onClick={() => this.handleGameModeSelect('single')}
                  loading={isLoading && gameMode === 'single'}
                  disabled={isAnyLoading}
                  icon={!isLoading ? <ThunderboltOutlined /> : undefined}
                  className="mobile-button"
                  style={{
                    minHeight: '64px',
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    border: 'none',
                    borderRadius: 'var(--radius-lg)',
                    fontSize: '1.2rem',
                    fontWeight: 600,
                    boxShadow: '0 8px 24px rgba(245, 158, 11, 0.3)'
                  }}
                >
                  {this.props.t('login.singlePlayer')}
                </Button>

                <Button
                  size="large"
                  onClick={() => this.handleGameModeSelect('multiplayer')}
                  disabled={isAnyLoading}
                  className="mobile-button"
                  style={{
                    minHeight: '64px',
                    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                    borderColor: '#6366f1',
                    color: 'white',
                    borderRadius: 'var(--radius-lg)',
                    fontSize: '1.2rem',
                    fontWeight: 600,
                    boxShadow: '0 8px 24px rgba(99, 102, 241, 0.3)'
                  }}
                >
                  {this.props.t('login.multiplayer')}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Room Selection (Multiplayer) */}
          {currentStep === 'roomselect' && (
            <div>
              <div style={{ marginBottom: 24 }}>
                <Button
                  type="text"
                  onClick={this.handleBackToGameMode}
                  style={{ color: 'var(--text-secondary)', padding: 0, marginBottom: 16 }}
                >
                  {this.props.t('login.back')}
                </Button>
                
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <Text style={{ color: 'var(--text-secondary)', fontSize: '1rem' }}>
                    {this.props.t('login.enterRoomName')}
                  </Text>
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <Input
                  ref={this.roomInputRef}
                  size="large"
                  placeholder={this.props.t('login.enterRoomNamePlaceholder')}
                  value={room}
                  onChange={(e) => this.setState({ room: e.target.value })}
                  onPressEnter={this.handleRoomJoin}
                  style={{
                    minHeight: '48px',
                    fontSize: '1rem',
                    borderRadius: 'var(--radius-md)',
                    padding: '12px 16px'
                  }}
                />
              </div>

              <Button
                type="primary"
                size="large"
                onClick={this.handleRoomJoin}
                loading={isLoading}
                disabled={!room.trim() || isAnyLoading}
                className="mobile-button"
                style={{
                  width: '100%',
                  minHeight: '48px',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                {isLoading ? this.props.t('login.joining') : this.props.t('login.joinRoom')}
              </Button>
            </div>
          )}

          {/* Username Modal for Guest */}
          <Modal
            title={this.props.t('login.chooseYourUsername')}
            open={this.state.showUsernameModal}
            onOk={this.handleUsernameConfirm}
            onCancel={() => this.setState({ showUsernameModal: false })}
            okText={this.props.t('login.joinGame')}
            cancelText={this.props.t('login.cancel')}
            centered
            okButtonProps={{
              disabled: !this.state.tempUsername.trim()
            }}
          >
            <div style={{ padding: '20px 0' }}>
              <Text style={{ 
                color: 'var(--text-secondary)', 
                fontSize: '0.95rem',
                display: 'block',
                marginBottom: 16
              }}>
                {this.props.t('login.enterDisplayName')}
              </Text>
              <Input
                size="large"
                placeholder={this.props.t('login.enterYourUsername')}
                prefix={<UserOutlined />}
                value={this.state.tempUsername}
                onChange={(e) => this.setState({ tempUsername: e.target.value })}
                onPressEnter={this.handleUsernameConfirm}
                maxLength={20}
                autoFocus
              />
            </div>
          </Modal>

        </Card>
      </div>
    );
  }
}

// Functional wrapper component to use hooks
const Login: React.FC = () => {
  const { t, i18n } = useTranslation();
  return <LoginComponent t={t} i18n={i18n} />;
};

export default Login;